/*
 * -----------------------------------------------------------------------
 *  <copyright file="edit-study-summary-conf-dialog.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Checkbox } from '@progress/kendo-react-inputs';

import { useBoolean, useTranslation } from 'hooks';
import { StudyFields } from 'queries/generated/fragments.generated';
import { GET_STUDY, SAVE_STUDY_SUMMARY_CONFIGURATION } from 'queries/studies';
import NotificationService from 'utils/notification';
import { SaveButton } from 'views/components/core/buttons';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import OutlinedWrapper from 'views/components/core/outlined-wrapper';

import 'views/components/admin/studies/study-settings-dialogs/study-settings-dialogs.scss';

const EditStudySummaryConfDialog = ({
    study,
    open,
    onClose,
    canEdit,
}: {
    study: StudyFields;
    open: boolean;
    onClose: Function;
    canEdit: boolean;
}) => {
    const { t } = useTranslation();
    const disabledSaveButton = useBoolean(true);

    const initialState = {
        numScreened: study?.studySummaryConfiguration.numScreened,
        numScreenFailed: study?.studySummaryConfiguration.numScreenFailed,
        numEnrolled: study?.studySummaryConfiguration.numEnrolled,
        numRandomised: study?.studySummaryConfiguration.numRandomised,
        numTreatmentDiscontinued: study?.studySummaryConfiguration.numTreatmentDiscontinued,
        numStudyDiscontinued: study?.studySummaryConfiguration.numStudyDiscontinued,
        numCompleted: study?.studySummaryConfiguration.numCompleted,
    };
    const [state, setState] = useState({ ...initialState });
    const [saveRiskSummary] = useMutation(SAVE_STUDY_SUMMARY_CONFIGURATION);

    useEffect(() => {
        if (open && study)
            setState({
                numScreened: study?.studySummaryConfiguration.numScreened,
                numScreenFailed: study?.studySummaryConfiguration.numScreenFailed,
                numEnrolled: study?.studySummaryConfiguration.numEnrolled,
                numRandomised: study?.studySummaryConfiguration.numRandomised,
                numTreatmentDiscontinued: study?.studySummaryConfiguration.numTreatmentDiscontinued,
                numStudyDiscontinued: study?.studySummaryConfiguration.numStudyDiscontinued,
                numCompleted: study?.studySummaryConfiguration.numCompleted,
            });
    }, [open, study]);

    const close = () => {
        disabledSaveButton.setTrue();
        setState({ ...initialState });
        onClose();
    };

    const onChangeCheckbox = (fieldName: string) => () => {
        disabledSaveButton.setFalse();
        setState({ ...state, [fieldName]: !(state as any)[fieldName] });
    };

    const successRequest = () => {
        NotificationService.showNotification(t('admin:studySuccessfulUpdated', { id: study.recordId }));
        onClose();
    };

    const errorRequest = () => {
        NotificationService.showNotification(t('errorMessage'));
        disabledSaveButton.setFalse();
    };

    const onUpdate = () => {
        disabledSaveButton.setTrue();
        saveRiskSummary({
            variables: {
                studyId: study.id,
                numScreened: state.numScreened,
                numScreenFailed: state.numScreenFailed,
                numEnrolled: state.numEnrolled,
                numRandomised: state.numRandomised,
                numTreatmentDiscontinued: state.numTreatmentDiscontinued,
                numStudyDiscontinued: state.numStudyDiscontinued,
                numCompleted: state.numCompleted,
            },
            refetchQueries: [{ query: GET_STUDY, variables: { id: study.id } }],
        })
            .then(() => {
                successRequest();
            })
            .catch(() => {
                errorRequest();
            });
    };

    return (
        <CustomDialog
            className="edit-study-summary-config-dialog"
            onClose={close}
            fullWidth
            scroll="paper"
            maxWidth="md"
            open={open}
        >
            <DialogTitle id="edit-study-summary-config-title">
                {t('admin:editStudySummaryConfiguration')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={close}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="edit-study-summary-config-dialog-content">
                <OutlinedWrapper label={t('admin:studyInfo')}>
                    <div className="check-box-wrapper">
                        <div className="column">
                            <p>
                                <Checkbox
                                    rounded="none"
                                    checked={state.numScreened}
                                    onChange={onChangeCheckbox('numScreened')}
                                    label={t('admin:screened')}
                                    data-testid="screened-checkbox"
                                    size="medium"
                                />
                            </p>
                            <p>
                                <Checkbox
                                    rounded="none"
                                    checked={state.numScreenFailed}
                                    onChange={onChangeCheckbox('numScreenFailed')}
                                    label={t('admin:screenFailed')}
                                    data-testid="screen-failed-checkbox"
                                    size="medium"
                                />
                            </p>
                            <p>
                                <Checkbox
                                    rounded="none"
                                    checked={state.numEnrolled}
                                    onChange={onChangeCheckbox('numEnrolled')}
                                    label={t('admin:enrolled')}
                                    data-testid="enrolled-checkbox"
                                    size="medium"
                                />
                            </p>
                        </div>
                        <div className="column">
                            <p>
                                <Checkbox
                                    rounded="none"
                                    checked={state.numRandomised}
                                    onChange={onChangeCheckbox('numRandomised')}
                                    label={t('admin:randomized')}
                                    data-testid="randomised-checkbox"
                                    size="medium"
                                />
                            </p>
                            <p>
                                <Checkbox
                                    rounded="none"
                                    checked={state.numTreatmentDiscontinued}
                                    onChange={onChangeCheckbox('numTreatmentDiscontinued')}
                                    label={t('admin:treatmentDiscontinued')}
                                    data-testid="treatment-discontinued-checkbox"
                                    size="medium"
                                />
                            </p>
                            <p>
                                <Checkbox
                                    rounded="none"
                                    checked={state.numStudyDiscontinued}
                                    onChange={onChangeCheckbox('numStudyDiscontinued')}
                                    label={t('admin:studyDiscontinued')}
                                    data-testid="study-discontinued-checkbox"
                                    size="medium"
                                />
                            </p>
                        </div>
                        <div className="column">
                            <p>
                                <Checkbox
                                    rounded="none"
                                    checked={state.numCompleted}
                                    onChange={onChangeCheckbox('numCompleted')}
                                    label={t('admin:completed')}
                                    data-testid="completed-checkbox"
                                    size="medium"
                                />
                            </p>
                        </div>
                    </div>
                </OutlinedWrapper>
            </DialogContent>
            <DialogActions
                className={`edit-study-summary-config-dialog-actions dialog-actions ${study ? '' : 'flex-end'}`}
            >
                {study && (study.created || study.updated) && <ModalUpdatedInfo item={study} hideCreated />}
                <SaveButton
                    id="edit-study-summary-config-save"
                    disabled={disabledSaveButton.value || !canEdit}
                    text="update"
                    onClick={onUpdate}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default EditStudySummaryConfDialog;
